<template>
  <div class="header-right-container mobile-nav">
    <div class="menu-icon">
      <img
        :src="
          appStore.currentMode() === DisplayMode.Light
            ? isMenuOpen
              ? iconXCloseLight
              : iconHamburgerLight
            : isMenuOpen
              ? iconXCloseDark
              : iconHamburgerDark
        "
        @click="toggleMenu"
      />
    </div>
  </div>
  <Teleport to="body">
    <Transition name="drop">
      <div
        v-show="isMenuOpen"
        class="mobile-menu"
        :style="{ '--header-h': headerHeight + 'px' }"
        tabindex="-1"
        ref="menuEl"
        @keydown="onOverlayKeydown"
        @click="onBackdropClick"
      >
        <nav id="mobile-menu" class="mobile-menu-content" role="dialog" aria-modal="true">
          <LanguageSelector
            :model="appStore.configuration?.languages"
            :key="componentKey"
            :compact-display="false"
          />

          <div class="menu-item"><ThemeSwitcher /></div>

          <div class="menu-item">
            <ButtonSimple
              :model="appStore.configuration?.components.header.buttonSimple"
              :alt="t('header.exportToPdfButton.alt')"
              :text="t('header.exportToPdfButton.text')"
              :click="
                () => {
                  closeMenu()
                  handleExportToPdfButtonClick()
                }
              "
              :isDisabled="false"
              :key="componentKey"
              :show="showExportToPdfButton"
            />
          </div>

          <div class="menu-item"><UserProfile @logout-click="onLogoutClick" /></div>
        </nav>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import iconHamburgerDark from '../assets/icons/dark/icon-hamburger.svg'
import iconHamburgerLight from '../assets/icons/light/icon-hamburger.svg'
import iconXCloseDark from '../assets/icons/dark/icon-x-close.svg'
import iconXCloseLight from '../assets/icons/light/icon-x-close.svg'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import ButtonSimple from './ButtonSimple.vue'
import UserProfile from './UserProfile.vue'
import { t } from '../composables/Translation'
import { useAppStore } from '../stores/appStore'
import { DisplayMode } from '../types/app/displayMode'
import { updateComponentKey } from '../composables/ComponentKey'
import { State } from '../types/app/state'
import type { Header } from '../types/configuration/Header'
import ThemeSwitcher from './ThemeSwitcher.vue'
import { useScrollLock } from '../composables/ScrollLock'
import LanguageSelector from './LanguageSelector.vue'

const isMenuOpen = ref(false)
const pushedForMenu = ref(false)

const appStore = useAppStore()

const props = defineProps<{
  model: Header | undefined
  headerHeight: number
}>()

function onLogoutClick(): void {}

function toggleMenu() {
  if (isMenuOpen.value) closeMenu()
  else openMenu()
}

function openMenu() {
  if (isMenuOpen.value) return
  isMenuOpen.value = true
  document.documentElement.classList.add('no-scroll')
  document.body.classList.add('no-scroll')
  history.pushState({ menuOverlay: true }, '')
  pushedForMenu.value = true
}

const onOverlayKeydown = (e: KeyboardEvent) => {
  if (!isMenuOpen.value) return
  if (e.key === 'Escape' || e.code === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    closeMenu()
  }
}

function closeMenu(opts: { fromBack?: boolean } = {}) {
  if (!isMenuOpen.value) return
  isMenuOpen.value = false
  document.documentElement.classList.remove('no-scroll')
  document.body.classList.remove('no-scroll')
  if (pushedForMenu.value && !opts.fromBack) {
    history.back()
  }
  pushedForMenu.value = false
}

const onKeyDown = (e: KeyboardEvent) => {
  if (!isMenuOpen.value) return
  if (e.key === 'Escape' || e.code === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    closeMenu()
  }
}

const onPopState = (_e: PopStateEvent) => {
  if (pushedForMenu.value) {
    closeMenu({ fromBack: true })
  }
}

const onBackdropClick = (e: MouseEvent) => {
  if (e.target === e.currentTarget) closeMenu()
}

onMounted(() => {
  window.addEventListener('keydown', onKeyDown, { capture: true })
  window.addEventListener('popstate', onPopState)
})

function handleExportToPdfButtonClick(): void {
  printConversation()
}

function printConversation() {
  window.print()
}

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown, { capture: true })
  window.removeEventListener('popstate', onPopState)

  if (pushedForMenu.value) {
    pushedForMenu.value = false
    history.back()
  }
})

useScrollLock(isMenuOpen)

let componentKey = updateComponentKey(props)
const state = computed(() => appStore.currentState)

const showExportToPdfButton = computed(() => state.value === State.Main)
</script>

<style lang="css" scoped>
.menu-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  padding-right: 10px;
}

.mobile-menu {
  position: fixed;
  left: 0;
  right: 0;
  top: var(--header-h, 56px);
  bottom: 0;
  z-index: 1000;
  background: var(--background-color);
  display: block;
}

.mobile-menu-content {
  height: 100%;
  padding: 16px 16px 24px;
  display: grid;
  grid-auto-rows: min-content;
  align-content: start;
  gap: 16px;
  overflow: auto;

  --vs-padding: 0px;
}

.menu-item {
  padding: 12px 0;
  border-radius: 8px;
}

.menu-item-btn {
  text-align: left;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 12px 0;
  font: inherit;
  color: var(--text-color);
  cursor: pointer;
  border-radius: 8px;
}
.menu-item-btn:hover {
  background: var(--text-background-color);
}

.mobile-menu:focus {
  outline: none;
}
.mobile-menu:focus-visible {
  outline: none;
}

.drop-enter-active,
.drop-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
  will-change: transform, opacity;
}

.drop-enter-from,
.drop-leave-to {
  opacity: 0;
  transform: translateY(calc(-1 * var(--header-h, 56px)));
  /* or: translateY(calc(-1 * var(--header-h, 56px) - 8px)); */
}
</style>
